import { useState } from "react";
import { Badge, Button, EmptyState, QueryStateBoundary } from "@/components/ui";
import type { AskOut, StudioAskStatus } from "@/api/types/studio";
import { ASK_STATUSES, ASK_STATUS_BADGE_VARIANT, ASK_STATUS_LABELS, pageLabel } from "@/api/types/studio";
import { useDecideAsk, useStudioAsks } from "@/api/hooks/useStudio";
import { useAuth } from "@/api/hooks/useAuth";
import { toastError, toastSuccess } from "@/components/ui/Toast";
import { getErrorMessage } from "@/api/client";

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

/** One ask card; admin gets the approve/decline controls on held asks. */
function AskCard({ ask, isAdmin }: { ask: AskOut; isAdmin: boolean }) {
  const decide = useDecideAsk();
  const [note, setNote] = useState("");
  const decidable = isAdmin && ask.status === "held_for_will";

  function decideWith(status: StudioAskStatus) {
    decide.mutate(
      { id: ask.id, status, decision: note.trim() || null },
      {
        onSuccess: () => toastSuccess(status === "declined" ? "Ask declined" : "Ask approved — the assistant will pick it up"),
        onError: (err) => toastError("Couldn't record the decision", getErrorMessage(err)),
      },
    );
  }

  return (
    <div className="rounded-lg border border-border bg-bg-card p-4" data-testid="studio-ask">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-text-primary break-words">{ask.title}</p>
          {ask.detail && <p className="mt-1 text-sm text-text-secondary break-words">{ask.detail}</p>}
          <p className="mt-1.5 text-xs text-text-muted">
            {ask.page && <span className="mr-2">{pageLabel(ask.page)}</span>}
            {formatDate(ask.created_at)}
            {ask.decision && <span className="ml-2 italic">“{ask.decision}”</span>}
          </p>
        </div>
        <Badge variant={ASK_STATUS_BADGE_VARIANT[ask.status]} size="sm">
          {ASK_STATUS_LABELS[ask.status]}
        </Badge>
      </div>

      {decidable && (
        <div className="mt-3 border-t border-border pt-3">
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Note for the assistant (optional)"
            aria-label={`Decision note for ${ask.title}`}
            className="mb-2 w-full rounded-md border border-border bg-bg-body px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-primary focus:ring-[3px] focus:ring-primary/15"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={() => decideWith("in_progress")} disabled={decide.isPending}>
              Approve
            </Button>
            <Button size="sm" variant="danger" onClick={() => decideWith("declined")} disabled={decide.isPending}>
              Decline
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Will's approval board: asks grouped by status in the contract's order.
 * Admin decides held_for_will items; owner sees chips only.
 */
export function AsksBoard() {
  const { data, isLoading, error, refetch } = useStudioAsks();
  const { role } = useAuth();
  const isAdmin = role === "admin";

  const asks = data?.items ?? [];
  const grouped = ASK_STATUSES.map((status) => ({
    status,
    items: asks.filter((a) => a.status === status),
  }));

  return (
    <QueryStateBoundary isLoading={isLoading} error={error} onRetry={() => void refetch()}>
      <div className="h-full min-h-0 space-y-6 overflow-y-auto pb-4" data-testid="studio-asks">
        {asks.length === 0 ? (
          <EmptyState
            icon="🗂️"
            title="No asks yet"
            description="When the assistant needs a decision or tracks a request for Nikki, it lands here."
          />
        ) : (
          grouped.map(({ status, items }) => (
            <section key={status}>
              <div className="mb-2 flex items-center gap-2">
                <h2 className="text-sm font-semibold text-text-primary">{ASK_STATUS_LABELS[status]}</h2>
                <span className="rounded-full bg-bg-muted px-2 py-0.5 text-xs font-medium text-text-secondary">
                  {items.length}
                </span>
              </div>
              {items.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border px-4 py-3 text-xs text-text-muted">
                  Nothing here.
                </p>
              ) : (
                <div className="space-y-2">
                  {items.map((ask) => (
                    <AskCard key={ask.id} ask={ask} isAdmin={isAdmin} />
                  ))}
                </div>
              )}
            </section>
          ))
        )}
      </div>
    </QueryStateBoundary>
  );
}
